import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {defer, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, distinctUntilChanged, finalize, switchMap, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {SwiperOptions} from 'swiper/types';
import Swiper, {FreeMode, Keyboard, Mousewheel} from 'swiper';
import {ModalInterface} from '../../modal.interface';
import {ModalStateEvent} from '../../modal-state-event';
import {ModalState} from '../../modal-state.enum';
import {TokenDataProviderService} from '../../../../core/services/token-data-provider.service';
import {CurrentUserViewModel} from '../../../../core/models/current-user.view-model';
import {SocialDataProviderService} from '../../../timeline/social-grid-list/social-data-provider.service';
import {ModalComponent} from '../../modal.component';
import {ScrollableModalListBase} from '../scrollable-modal-list.base';
import {SocialListAdapter} from './social-list-adapter.interface';
import {SocialListAdapterRegistryService} from './social-list-adapter-registry.service';
import {SocialListModalConfig, SocialListReactionRemoval} from './social-list-modal-config.interface';

Swiper.use([FreeMode, Keyboard, Mousewheel]);

@Component({
  selector: 'app-social-list-modal',
  templateUrl: './social-list-modal.component.html',
  styleUrls: ['./social-list-modal.component.scss']
})
export class SocialListModalComponent extends ScrollableModalListBase<any> implements OnInit, AfterViewInit, OnDestroy, ModalInterface {
  private readonly subscriptions: Subscription[] = [];
  private page = 0;
  private destroyed = false;
  private navigating = false;
  private reactionBusy = false;
  private removingReaction = false;
  private reactionRemovalError = false;
  public adapter: SocialListAdapter<any, any, any>;

  @Input() public data: SocialListModalConfig;
  @ViewChild('listContainer') private readonly listContainer?: ElementRef<HTMLElement>;

  public currentUser: CurrentUserViewModel;
  public state: Observable<ModalStateEvent>;
  public subject: Subject<ModalStateEvent>;
  public renderedItems: any[] = [];
  public swiperConfig: SwiperOptions;
  public reactionRemoval?: SocialListReactionRemoval;

  constructor(
    private readonly adapterRegistry: SocialListAdapterRegistryService,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly router: Router,
    protected readonly modalComponent: ModalComponent,
    protected readonly cdr: ChangeDetectorRef,
    public readonly socialDPS: SocialDataProviderService,
  ) {
    super();
  }

  get parentModalRendered(): boolean {
    return this.modalComponent.rendered;
  }

  get itemComponent(): any {
    return this.adapter?.itemComponent;
  }

  get emptyTranslationKey(): string {
    return this.adapter?.emptyTranslationKey || '';
  }

  get trimmedTranslationKey(): string {
    return this.adapter?.trimmedTranslationKey || '';
  }

  public ngOnInit(): void {
    this.adapter = this.adapterRegistry.get(this.data.listType);
    this.initSwiperConfig();

    this.subscriptions.push(
      this.tokenDPS.currentUser.subscribe((currentUser: CurrentUserViewModel) => {
        this.currentUser = currentUser;
        if (this.currentUser?.['@id']) {
          this.fetchCurrentUserFollows();
          this.fetchCurrentUserLikes();
        }
      })
    );

    if (this.data.reactionActions) this.watchReactions();
    else this.loadPage(1, true);
  }

  public ngAfterViewInit(): void {
    this.initScrollableList();
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.destroyScrollableList();
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.handleResize();
  }

  public navigateTo = async (item: any): Promise<void> => {
    const slug = item?.author?.slug || item?.follower?.slug || item?.user?.slug;
    if (!slug || this.navigating || this.destroyed) return;

    const target = this.router.createUrlTree(['/', slug, 'profile']);
    if (this.router.isActive(target, {paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored'})) {
      this.subject.next(new ModalStateEvent(ModalState.SUCCESS));
      return;
    }
    this.navigating = true;
    try {
      const navigated = await this.router.navigateByUrl(target);
      if (navigated && !this.destroyed) this.subject.next(new ModalStateEvent(ModalState.SUCCESS));
    } catch {
      // A failed or cancelled navigation leaves this list available for retry.
    } finally {
      this.navigating = false;
    }
  };

  private watchReactions(): void {
    this.subscriptions.push(this.data.reactionActions.state$.pipe(
      tap(state => {
        this.reactionBusy = state.busy;
        this.updateReactionRemoval();
      }),
      distinctUntilChanged((previous, current) => previous.reactions === current.reactions),
      switchMap(state => this.adapter.loadPage({...this.data, initialData: state.reactions}, 1, this.pageRows)),
    ).subscribe(({items}) => {
      // Replace the snapshot: merging would keep rows removed by HTTP/Mercure.
      this.renderedItems = items;
      this.initialLoading = false;
      this.hasMore = false;
      this.cdr.markForCheck();
    }));
  }

  private updateReactionRemoval(): void {
    this.reactionRemoval = {
      busy: this.reactionBusy || this.removingReaction,
      error: this.reactionRemovalError,
      remove: this.removeOwnReaction,
    };
    this.cdr.markForCheck();
  }

  private removeOwnReaction = (): void => {
    if (!this.data.reactionActions || this.reactionBusy || this.removingReaction || this.destroyed) return;
    this.removingReaction = true;
    this.reactionRemovalError = false;
    this.updateReactionRemoval();
    this.subscriptions.push(defer(() => this.data.reactionActions.removeOwn()).pipe(finalize(() => {
      if (this.destroyed) return;
      this.removingReaction = false;
      this.updateReactionRemoval();
    })).subscribe({error: () => {
      this.reactionRemovalError = true;
    }}));
  };

  public trackByItem = (index: number, item: any): string => {
    return this.adapter?.trackBy(index, item) || `${index}`;
  };

  protected loadMoreItems(): void {
    if (this.loadingMore || !this.hasMore) {
      return;
    }
    this.loadPage(this.page + 1);
  }

  protected mergeItems(existingItems: any[], incomingItems: any[]): any[] {
    return this.adapter.mergeItems(existingItems, incomingItems);
  }

  protected get listContainerElement(): HTMLElement | null {
    return this.listContainer?.nativeElement || null;
  }

  private loadPage(page: number, initial = false): void {
    if (this.loadingMore || (!initial && !this.hasMore && this.page > 0)) {
      return;
    }

    if (initial) {
      this.initialLoading = true;
      this.renderedItems = [];
      this.unloadedItemsCount = 0;
    } else {
      this.loadingMore = true;
    }

    this.subscriptions.push(
      this.adapter.loadPage(this.data, page, this.pageRows).pipe(
        catchError(() => of({items: [], hasMore: false, loadedPageCount: 1}))
      ).subscribe(({items, hasMore, loadedPageCount}) => {
        this.page = page === 1 ? Math.max(loadedPageCount || 1, 1) : page;
        this.hasMore = hasMore;
        this.renderedItems = this.appendRenderedItems(this.renderedItems, items);
        this.initialLoading = false;
        this.loadingMore = false;
        this.cdr.detectChanges();
        this.tryLoadNextIfNotScrollable();
      })
    );
  }

  private fetchCurrentUserFollows(): void {
    this.subscriptions.push(this.socialDPS.fetchCurrentUserFollows(this.currentUser).subscribe());
  }

  private fetchCurrentUserLikes(): void {
    this.subscriptions.push(this.socialDPS.fetchCurrentUserLikes(this.currentUser).subscribe());
  }

  private initSwiperConfig(): void {
    this.swiperConfig = {
      breakpoints: {
        320: {
          slidesPerView: 5,
          spaceBetween: 5,
        },
        480: {
          slidesPerView: 6,
          spaceBetween: 5,
        },
        720: {
          slidesPerView: 8,
        }
      },
      autoHeight: true,
      freeMode: {
        enabled: true,
        sticky: true,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: false,
      },
      mousewheel: {
        invert: true,
      },
    };
  }
}
